import {
  // https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-8.html#type-only-imports-and-export
  type SharedHelperOptions,
  type LoginFunctionOptions,
  type SharedHelperUtilities,
  normalizeFieldMap,
} from '../plugin.ts'
import type { User as OidcUser } from 'oidc-client-ts'
import {
  sentryException,
  loginSentryCodes,
} from '../sentry.js'
import { Severity } from '@sentry/types'
import getOidcSettings, {
  type OidcSettingsOptions,
} from './config.ts'
import Vuex from 'vuex'
import Vue from 'vue'

import {
  vuexOidcCreateStoreModule,
  vuexOidcProcessSignInCallback,
  vuexOidcCreateUserManager,
  type VuexOidcErrorPayload,
} from 'vuex-oidc'

// This is a holdover from gsg-payhub-ui-mono where we have ts shims to modify
// the vue type with vue/compat. We don't need that globally here so I'm not
// porting the ts shim.
// Additionally we can't use a comment to ignore the ts error here as ts doesn't
// throw an error when it's compiled in the psp monorepo. (Remember we're
// shipping and importing uncompiled modules.)
// As soon as were off the Vue 3 compat build this should be removed.
(Vue as typeof Vue & { use: (unknown) => void }).use(Vuex)

/**
 * Checks whether the current URL is a callback URL, and if so finalizes the
 * login callback process *and redirects the page* if so.
 *
 * @function handleOidcCallbackPage
 * @return {Promise<boolean>} - Returns false if oidc processes or redirect
 * failed, true otherwise.
 */
export const handleOidcCallbackPage = async ({
  env,
  oidcClient,
  baseRoute,
}: OidcSettingsOptions): Promise<boolean> => {
  if (/\/callback(?:[/#?]|$)/.test(window.location.href)) {
    // This is the callback in the main window
    try {
      const settings = getOidcSettings({
        env,
        baseRoute,
        oidcClient,
      })

      // TODO: PSC-20491 - Remove these casts when this issue is resolved
      // https://github.com/perarnborg/vuex-oidc/issues/210
      const redirectPath = await vuexOidcProcessSignInCallback(settings) as unknown as string
      const oidcUserManager = vuexOidcCreateUserManager(settings)
      await oidcUserManager.getUser()

      // User just successfully logged in
      window.history.replaceState({}, document.title, clearSilentAuthSearchParam(redirectPath))
      return true
    }
    catch (error_) {
      const error = error_ instanceof Error
        ? error_
        : new Error(String(error_))

      if (
        // User was redirected through the top level silent auth, and should
        // not be redirected to the login-error page for any exceptions
        hasTriedSilentAuth() ||
        // User has just returned from a silent login attempt, and is not logged in
        isUnauthenticatedError(error)
      ) {
        const redirectPath = sessionStorage.getItem('vuex_oidc_active_route') || '/'
        console.info(error, 'Handled error during oidcCallback. Silenced because ' +
          hasTriedSilentAuth()
          ? 'callback executed during top level silent auth'
          : 'user is anonymous, this error was expected.',
        )
        window.history.replaceState({}, document.title, clearSilentAuthSearchParam(redirectPath))
        return true
      }

      // Some other, unexpected error case
      error.message = 'handleOidcCallbackPage Authentication error: ' + error.message + '. Active url: ' + window.location.href
      console.error(error)
      sentryException(error)
      window.history.replaceState({}, document.title, '/')
      return false
    }
  }

  // This is Auth0-specific cruft, which we need because it doesn't do
  // "end_session" by the OIDC book. (If it did, vuex-oidc would handle this
  // automatically.)
  // XXX: Should we remove it then?
  if (/\/logout(?:[/#?]|$)/.test(window.location.href)) {
    const url = sessionStorage.getItem('vuex_oidc_active_route') || '/'
    window.history.replaceState({}, document.title, url)
  }

  return true
}

const isVuexOidcErrorPayload = (error: unknown): error is VuexOidcErrorPayload =>
  Boolean((error as VuexOidcErrorPayload)?.context) &&
  Boolean((error as VuexOidcErrorPayload)?.error)

const isInvalidGrantError = (error?: unknown) => (error as { error?: string })?.error === 'invalid_grant'

const isUnauthenticatedError = (error: unknown) => String(error).match(/(Login required)|(End-User authentication (is required|could not be obtained))/)

const handleOidcError = (error?: unknown): void => {
  // This is never a user-visible error, but if something unexpected
  // blew up, we want the Sentry notification about it.

  if (!error) {
    return
  }

  let ignoreLog = ''

  // This is the top level silent auth redirect needed to authenticate users
  // across domains. Any errors that occur during this redirect should be
  // logged in sentry, but should not cause the user to be directed to the
  // login-error page.
  ignoreLog ||= hasTriedSilentAuth() ? 'Error occurred during top level cross domain silent redirect.' : ''
  // This error is thrown if a user's refresh token has expired; ignore.
  // Unfortunately, the error does not include any more details to specify
  // to ignore only refresh token failures.
  ignoreLog ||= isInvalidGrantError(error) ? 'Error was an invalid grant error.' : ''
  // The "Login required" error is normal processing when the user isn't
  // logged in, so we don't notify in that case.
  ignoreLog ||= isUnauthenticatedError(error)
    ? 'Error was an expected error thrown for anonymous users.'
    : ''

  if (ignoreLog) {
    console.info(error, 'handleOidcError not sending error to sentry. ' + ignoreLog)
    return
  }

  console.error(error)
  if (isVuexOidcErrorPayload(error)) {
    sentryException({
      name: error.context,
      message: error.error,
    })
    return
  }
  // For some reason, error.context and error.error seem to be undefined for
  // some high volume alerts. Lets see if this gets us any details...
  sentryException({
    name: error.toString(),
    message: error.toString(),
  })
}

export type FullHelperExclusiveOptions = OidcSettingsOptions &
  {
    getAuthQueryParams: () => Record<string, string>
  }

export type FullHelperOptions = SharedHelperOptions & FullHelperExclusiveOptions

export type FullLoginHelpers = SharedHelperUtilities & {
  // *Do not* add any properties to this local type that are shared with
  // ExternalLoginHelpers. Those need to go in SharedHelperUtilities.
  handleOidcCallbackPage: () => ReturnType<typeof handleOidcCallbackPage>
}

const envToUrlPrefix = {
  'prod': '',
  'demo': 'demo.',
  'beta': 'beta.',
  'stage': 'stage.',
  'test': 'test.',
  'dev': 'test.',
}
/**
 * This accepts an environment string. If the string is not recognized, this
 * no-ops. The env is matched to the env prefix used to store the oidc refresh
 * token in local storage. If this item exists in local storage, it will be
 * deleted, and this returns true. This no-ops and returns false otherwise.
 */
const removeOidcUserFromLocalStorage = (env: string): boolean => {
  const prefix = envToUrlPrefix[env]
  if (!prefix) {
    return false
  }

  // This is the key used in our oidc deps. It might be configurable or
  // something we can access without the magic string...
  const localStorageKey = `oidc.user:https://${prefix}govhub.com/svc/login/oidc:payhub-spa`
  try {
    if (localStorage.getItem(localStorageKey)) {
      localStorage.removeItem(localStorageKey)
      return true
    }
  }
  catch {
    // localstorage access may fail, but we won't care about these errors.
    // There's nothing we can do to prevent this from failing, and the result
    // would just mean the user has to click the profile icon to login.
  }
  return false
}

/**
 * Creates utilities for "full" login mode, which supports a full app login
 * experience (redirecting the user to a login page, handling the callback, and
 * handling silent token renewals).
 */
export default ({
  oidcClient,
  getAuthQueryParams,
  env,
  baseRoute,
  user,
  beforeLogin,
  onUserLoaded,
  onUserSignedOut,
  onLoginFailure,
  userFieldMap = {},
}: FullHelperOptions): FullLoginHelpers => {
  // Format the structure for consistency
  const normalizedFieldMap = normalizeFieldMap(userFieldMap)

  const setPublicUserFields = (oidcUser: OidcUser) => {
    // Update the public user object with fields from the oidcUser
    for (const [appField, { current, deprecated }] of Object.entries(normalizedFieldMap)) {
      // Fallback to deprecated fields if the current field name isn't set
      let newValue
      for (const field of [current, ...deprecated]) {
        if (oidcUser.profile[field]) {
          newValue = oidcUser.profile[field]
          break
        }
      }
      // Unsets local field if no value was set in oidc
      user[appField] = newValue
    }

    user.id = oidcUser.profile.sub
    user.email = oidcUser.profile.email
    user.getAccessToken = () => oidcUser.access_token as (string | undefined)
  }

  const store = new Vuex.Store({
    modules: {
      oidcStore: vuexOidcCreateStoreModule(
        getOidcSettings({
          env,
          baseRoute,
          oidcClient,
        }),

        {
          dispatchEventsOnWindow: false,
        },

        {
          userLoaded: (oidcUser: OidcUser) => {
            // Update the global `user` object fields based on the new OidcUser
            setPublicUserFields(oidcUser)

            onUserLoaded(user)
          },

          userUnloaded: () => user.reset(),

          userSignedOut: () => {
            user.reset()
            onUserSignedOut()
          },

          accessTokenExpired: async () => {
            user.reset()

            // To unload all user-specific data, just reload the page.
            // It seems risky to try to unload it from each module,
            // although that seem cleaner in principle. This should
            // happen rarely, regardless.
            if (store.getters.oidcUser) {
              await store.dispatch('removeOidcUser')

              // .reload() doesn't work properly with history in FF
              // eslint-disable-next-line no-self-assign
              window.location.href = window.location.href
            }
          },

          oidcError: (oidcError) => {
            onLoginFailure(
              (oidcError?.error && !(oidcError.error instanceof Error))
                ? new Error(oidcError.error)
                // Typescript can't properly narrow the property type so the
                // assertion is necessary
                : oidcError?.error as Error | undefined)
          },

          silentRenewError: handleOidcError,
          automaticSilentRenewError: handleOidcError,
        },
      ),
    },
  })

  /**
   * This will resolve once we know whether the user is logged in or not. That
   * is determined by vuex-oidc code in a hidden iframe.
   */
  let resolveAuthPromise
  const authPromise = new Promise<void>(resolve => {
    resolveAuthPromise = resolve
  })

  return {
    initAuth: async () => {
      /**
       * vuex-oidc can do two things to silently connect to an active session:
       *
       *   a) Load an active session from localStorage and attempt to refresh it
       *   using a refresh token.
       *   b) Redirect to login/authorize?prompt=none in a hidden iframe.
       *
       * Because localStorage is scoped by domain, (a) will never work when
       * moving between domains. E.g. when we have a user in Sacramento
       * navigating between county-taxes.net departments and govhub.com
       * departments, they will need to rely on (b) to attach to the login
       * session.
       *
       * But, in certain browsers (currently Safari, Firefox, Chrome Incognito),
       * (b) will only work on govhub.com sites because 3rd party cookies are
       * disabled and the login service is on the govhub.com domain. So, in
       * these browsers the login session gets lost when navigating from
       * govhub.com to county-taxes.net.
       *
       * In that context, we need to add a third option:
       *
       *   c) Do a top-level redirect to login/authorize?prompt=none.
       */

      //
      // The flow here is:
      //
      // 1. If we have a valid user or refresh token in localStorage: try (a)
      //    then (b). This is the standard flow. If successful, resolve and
      //    return.
      //
      //    If we tried this and it wasn't successful there are two scenarios:
      //
      //    A. There was a refresh token but it was expired (or a synonymous
      //       issue occurred). This is a an invalid_grant error. In that case
      //       we want to wipe the user form localStorage and try to auth one
      //       more time. It can still succeed via the iframe.
      //
      //    B. This browsing context doesn't support 3rd party cookies so the
      //       iframe attempt failed. This is our worse case so we'll proceed to
      //       (c), redirect the parent page to try a silent auth again.
      //
      // 2. Try to auth the user.
      //
      //    A. If on the same domain as Login Service, try the standard flow
      //       (potentially for the second time; not retrying invalid grants).
      //       Resolve and return.
      //
      //    B. If not on the same domain as Login Service, try (c): redirect the
      //       parent page to try a top-level silent auth.
      //

      // This doesn't actually initialize the store, for some reason - we will
      // need to use an "authenticate" method to make that happen
      const oidcUser = await store.dispatch('getOidcUser')

      // If localStorage has an active user or refresh token then we'll attempt
      // a silent login (a).
      if (oidcUser && (!oidcUser.expired || oidcUser.refresh_token)) {
        // This will either use the user (if not expired) or attempt refresh.
        // If refresh token is expired, we catch and discard the expired token
        // afterward. Other errors are propagated.

        try {
          await store.dispatch('authenticateOidcSilent')
        }
        catch (error) {
          if (isInvalidGrantError(error)) {
            removeOidcUserFromLocalStorage(env)
          }
          else {
            // Errors other than invalid grants can still be retried because
            // they could be caused by our cross-origin cookie problems.
            // (Theoretically non-invalid_grant, non-cross-origin failures
            // probably shouldn't be retried but we aren't distinguishing
            // those.)
            handleOidcError(error)
          }
        }

        // If the attempt was successful resolve and return
        if (store.getters.oidcAccessToken) {
          // Clear silent auth flags if they existed
          clearSilentAuthFlags()
          // This could probably be moved into the above try block but I'd like
          // to leave room for the possibility that a non-fatal error was thrown
          // and the user *might* be able to proceed.
          return resolveAuthPromise()
        }
      }

      // If on the same domain as Login Service, we can use
      // authenticateOidcSilent to try the iframe approach for attaching to
      // existing server-side session - that's (b). (There's currently no way to have a
      // refresh_token for (a) without localStorage, in which case we've already
      // tried that once.)
      // Technically this also works on browsers that don't block tracking
      // cookies, but we don't want to try to guess which browsers those are.
      if (isOnLoginServiceOrigin()) {
        return store.dispatch('authenticateOidcSilent')
          .catch(handleOidcError)
          .then(resolveAuthPromise)
      }

      // Otherwise (e.g. on county-taxes.net) fall back to a full-page redirect.
      // This might only be needed on cross-domain sites, since we would expect
      // the localStorage mechanism to already handle session persistence for
      // single-domain sites.

      // Prevent infinite loops.
      //
      // We need a reliable way of detecting that we've already tried this.
      // Currently we do two separate checks:
      //
      // 1. Set a flag in SessionStorage
      // 2. Set a query param (which is cleared above)
      if (hasTriedSilentAuth()) {
        clearSilentAuthFlags()
        resolveAuthPromise()
        return
      }

      // Not an infinite loop so go ahead and try the top-level redirect
      try {
        setSilentAuthFlags()
      }
      catch (error) {
        // This is rough and I don't think we currently have a good solution for
        // it. If we can't set the flags then we can't prevent a redirect loop.
        sentryException(
          new Error('Unable to prevent infinite login loop during cross-domain public site oidc.'),
          {
            code: loginSentryCodes.LOGIN_X_DOMAIN_FAIL,
            // Probably either Fatal or Critical. I could see downgrading it.
            level: Severity.Critical,
          },
        )
        return
      }
      // Redirect the parent page and try a silent login. (If it succeeds the
      // user will be redirected back logged in. If it fails they'll be silently
      // redirected back unauthed, and our flags will prevent an infinite loop.)
      return store.dispatch('authenticateOidc', {
        redirectPath: window.location.href,
        options: {
          extraQueryParams: {
            ...getAuthQueryParams?.(),
            'origin_url': window.location.href,
            'prompt': 'none',
          },
        },
      })
    },

    authPromise,
    /**
     * Launches the OIDC sign in/sign up workflow.
     */
    logIn: ({
      signup,
    }: LoginFunctionOptions = {}): Promise<void> => {
      beforeLogin()
      return store.dispatch('authenticateOidc', {
        redirectPath: window.location.href,
        options: {
          extraQueryParams: {
            ...getAuthQueryParams?.(),
            'origin_url': window.location.href,
            ...(signup ? { 'action': 'signup' } : {}),
          },
        },
      })
    },

    logOut: async (redirectUri?: string): Promise<void> => {
      if (redirectUri && typeof redirectUri !== 'string') {
        console.warn(`@grantstreet/login: redirectUri must be a string. ${typeof redirectUri} given.`)
        redirectUri = undefined
      }

      const oidcUser = await store.dispatch('getOidcUser')

      // If id_token is not accessible then the user data may have been removed or
      // user is already logged out. See PSC-10593
      if (!oidcUser?.id_token) {
        // Try to re-fetch oidcUser
        try {
          await store.dispatch('authenticateOidcSilent')
        }
        // If re-fetch fails, we assume the user is already logged out
        catch (error) {
          console.warn('could not get user - skipping provider logout')
          // We need to make sure page reloads to E.g. stop showing user data on
          // page
          window.location.reload()
          return
        }
      }
      return store.dispatch('signOutOidc', {
        // May be null
        'post_logout_redirect_uri': redirectUri || window.location.href,
      })
    },

    /**
     * Update the Login Service profile and public User instance.
     */
    updateUserData: async (data: {[key: string]: string | undefined}) => {
      // Get the current user to update
      const oidcUser = await store.dispatch('getOidcUser')

      // Map data to configured oidc fields.
      for (const [appField, value] of Object.entries(data)) {
        // If someone passes arbitrary values that aren't defined during setup
        // when we shouldn't be storing them in the service.
        if (!normalizedFieldMap[appField]) {
          continue
        }

        const { current, deprecated } = normalizedFieldMap[appField]

        oidcUser.profile[current] = value
        // Override any deprecated field names for this now that a new value
        // has been set.
        for (const field of deprecated) {
          oidcUser.profile[field] = undefined
        }
      }

      // Post the update
      await store.dispatch('storeOidcUser', oidcUser)

      // Update local User instance
      setPublicUserFields(oidcUser)
    },

    handleOidcCallbackPage: () => handleOidcCallbackPage({ oidcClient, env, baseRoute }),
  }
}

// Internal: whether we can rely on the hidden-iframe redirect
const isOnLoginServiceOrigin = () => /^https:\/\/(?:[^.]+\.)?govhub\.com(?:\W|$)/.test(window.location.href)

// Internal: functions to handle tracking state to avoid redirect cycles
const silentAuthKey = 'tried_silent_auth'

const clearSilentAuthSearchParam = (path: string): string => {
  try {
    const url = new URL(path)
    url.searchParams.delete(silentAuthKey)
    return url.toString()
  }
  catch {}
  // If we couldn't construct a valid URL we'll have to skip it
  return path
}

const hasTriedSilentAuth = () =>
  Boolean(window.sessionStorage.getItem(silentAuthKey)) ||
  new URLSearchParams(window.location.search).has(silentAuthKey)

const clearSilentAuthFlags = () => {
  window.sessionStorage.removeItem(silentAuthKey)
  window.history.replaceState(window.history.state, document.title, clearSilentAuthSearchParam(window.location.href))
}

const setSilentAuthFlags = () => {
  let success = false
  // Theoretically this can throw if there's no more disk space. I doubt that
  // will ever happen but just in case we'll handle for it.
  try {
    window.sessionStorage.setItem(silentAuthKey, '1')
    success = true
  }
  catch {}

  // The url constructor can throw. Hopefully window.location is never an
  // invalid URL but let's not take the chance
  try {
    const url = new URL(window.location.href)
    url.searchParams.append(silentAuthKey, '1')
    window.history.replaceState(window.history.state, document.title, url)
  }
  catch {
    if (!success) {
      throw new Error('Cannot set silent auth flags.')
    }
  }
}
